/**
 * 题2 sh2023q24 — 2023 上海中考 Q24「顶点式与平移」。
 *
 * 直线 y = ¾x + 6 交 x 轴于 A、y 轴于 B；顶点 C 在线段 AB 上的抛物线
 * M：y = ax² + bx + c 过 B。平移 M 得 N：C→P(p,0)、B→D，CD∥x 轴，N 过 B。
 *
 * 全部由公式推导：A 由 x 截距，B 由 y 截距；m 由 CD∥x 轴条件
 * ¾m+6 = 6-(¾m+6)；a 由 am=-3/4；M 由顶点式展开；p 由 N 过 B 解 p²=32。
 */

import { evalLine, evalQuad, pt, quadFromVertex, vertexOf, xInterceptOf } from "../math/func";
import type { Line2, Quad } from "../math/func";
import type { ProblemDef, SceneDef } from "./types";

/** 直线 y = ¾x + 6。 */
export const lineAB23: Line2 = { k: 3 / 4, b: 6 };

/** (1) A 为 x 截距，B 为 y 截距。 */
const A = pt(xInterceptOf(lineAB23), 0); // (-8, 0)
const B = pt(0, lineAB23.b); // (0, 6)

/* (3) CD∥x 轴且 P 在 x 轴上：B、C 下移距离相同 → yC = 6 - yC → yC = 3。 */
const yC = lineAB23.b / 2; // 3
const m = (yC - lineAB23.b) / lineAB23.k; // -4
/* (2) M 过 B：am² + ¾m = 0，m≠0 → am = -3/4。 */
const aM = -3 / (4 * m); // 3/16
const C = pt(m, evalLine(lineAB23, m)); // (-4, 3)

/** 抛物线 M：y = (3/16)x² + (3/2)x + 6（顶点式展开）。 */
export const parabolaM23: Quad = quadFromVertex(aM, C);

/* N：y = (3/16)(x - p)² 过 B(0,6) → (3/16)p² = 6 → p = ±4√2。 */
const pAbs = Math.sqrt(B.y / aM); // 4√2
const P1 = pt(pAbs, 0);
const P2 = pt(-pAbs, 0);
/** N（p = +4√2）。 */
export const parabolaN23plus: Quad = quadFromVertex(aM, P1);
/** N（p = -4√2）。 */
export const parabolaN23minus: Quad = quadFromVertex(aM, P2);
/* D：B 按 C→P 同一平移向量移动，CD∥x 轴。 */
const D1 = pt(B.x + (P1.x - C.x), B.y + (P1.y - C.y)); // (4√2+4, 3)
const D2 = pt(B.x + (P2.x - C.x), B.y + (P2.y - C.y)); // (-4√2+4, 3)

const scene12: SceneDef = {
  id: "s12",
  tab: "(1)(2) 求抛物线 M",
  caption:
    "A(-8,0)、B(0,6)。设 C(m, ¾m+6)，由 M 过 B 得 am=-3/4，展开顶点式得 " +
    "y=ax²+(3/2)x+6 —— b=3/2，c=6。图中取 m=-4（第(3)问定出的值），顶点 C(-4,3)。",
  view: { xMin: -10, xMax: 3, yMin: -1.5, yMax: 8 },
  curves: [
    { kind: "line", id: "ab", line: lineAB23, tone: "cyan" },
    { kind: "quad", id: "M", quad: parabolaM23, tone: "gold" },
  ],
  points: [
    { name: "A", at: A, showCoords: true, dx: -6, dy: 18 },
    { name: "B", at: B, showCoords: true, dx: 14, dy: -4 },
    { name: "C", at: C, emphasis: true, showCoords: true, dx: -8, dy: -18 },
  ],
};

const scene3: SceneDef = {
  id: "s3",
  tab: "(3) 平移得 N",
  caption:
    "CD∥x 轴、P 在 x 轴上 → m=-4，a=3/16。N：y=(3/16)(x-p)² 过 B(0,6) → p=±4√2。" +
    "虚影为 M，绿/红两条为 p=±4√2 的 N；B 按 C→P 的平移向量移到 D（CD∥x 轴）。",
  view: { xMin: -10, xMax: 11.5, yMin: -1.5, yMax: 8.5 },
  curves: [
    { kind: "line", id: "ab", line: lineAB23, tone: "dim", dashed: true },
    { kind: "quad", id: "M", quad: parabolaM23, tone: "dim", dashed: true },
    { kind: "quad", id: "N1", quad: parabolaN23plus, tone: "green" },
    { kind: "quad", id: "N2", quad: parabolaN23minus, tone: "red" },
  ],
  points: [
    { name: "A", at: A, tone: "dim", dx: -6, dy: 18 },
    { name: "B", at: B, showCoords: true, dx: 14, dy: -4 },
    { name: "C", at: C, showCoords: true, dx: -14, dy: -14 },
    { name: "P1", at: P1, text: "P₁", tone: "green", dx: 0, dy: 18 },
    { name: "P2", at: P2, text: "P₂", tone: "red", dx: 0, dy: 18 },
    { name: "D1", at: D1, text: "D₁", emphasis: true, dx: 4, dy: -16 },
    { name: "D2", at: D2, text: "D₂", emphasis: true, dx: -10, dy: -16 },
  ],
  segments: [
    { a: C, b: D1, tone: "gold", dashed: true },
    { a: C, b: D2, tone: "gold", dashed: true },
  ],
};

export const sh2023q24: ProblemDef = {
  id: "sh2023q24",
  title: "2023·Q24 顶点式与平移",
  statement:
    "在平面直角坐标系 xOy 中，直线 y=¾x+6 与 x 轴交于点 A、与 y 轴交于点 B，" +
    "点 C 在线段 AB 上，以 C 为顶点的抛物线 M：y=ax²+bx+c 经过点 B。" +
    "（1）求 A、B 的坐标；（2）求 b、c 的值；（3）平移 M 至 N，C、B 分别平移至 P、D，" +
    "联结 CD 且 CD∥x 轴，若 P 在 x 轴上且 N 过点 B，求 N 的函数解析式。",
  givens: [
    { label: "直线", value: "y = ¾x + 6" },
    { label: "顶点 C", value: "在线段 AB 上" },
    { label: "M", value: "过点 B" },
  ],
  scenes: [scene12, scene3],
  solution: [
    "（1）x=0 时 y=6，故 B(0,6)；y=0 时 x=-8，故 A(-8,0)。",
    "（2）设 C(m, ¾m+6)，则可设抛物线为 y=a(x-m)²+¾m+6。M 过 B(0,6)：" +
      "am²+¾m+6=6，由 m≠0 得 am=-3/4。代入整理得 y=ax²+(3/2)x+6，故 b=3/2，c=6。",
    "（3）设 P(p,0)。由 CD∥x 轴，B、C 向下平移的距离相同：¾m+6=6-(¾m+6)，" +
      "解得 m=-4；由 am=-3/4 得 a=3/16。故 N：y=(3/16)(x-p)²。",
    "将 B(0,6) 代入得 (3/16)p²=6，p=±4√2。所以 N 的解析式为 " +
      "y=(3/16)(x-4√2)² 或 y=(3/16)(x+4√2)²。",
  ],
  answer: "b=3/2, c=6 ｜ N: y=(3/16)(x±4√2)²",
  computed: {
    ax: A.x,
    by: B.y,
    m,
    a: aM,
    b: parabolaM23.b,
    c: parabolaM23.c,
    yC,
    pPlus: pAbs,
    pMinus: -pAbs,
    vertexX: vertexOf(parabolaM23).x,
    n1AtB: evalQuad(parabolaN23plus, B.x),
    n2AtB: evalQuad(parabolaN23minus, B.x),
    d1x: D1.x,
    d1y: D1.y,
  },
};
